import { Component, Input } from '@angular/core';
import { FormArray, FormGroup, UntypedFormControl } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { WellType } from 'src/app/shared/models/wellType.model';
import { CustomTempProfile, TemperaturePoint, TemperatureProfileType } from '../../models/temperature-profile.model';
import { validateCustomProfile } from '../../../../../shared/validators/custom-profile.validator';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { UdtService } from 'src/app/shared/services/udt.service';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { lastValueFrom } from 'rxjs';
import { UnitsGridCommitEvent } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import {
  createInitialCustomTemperaturePoints,
  createCustomTempProfileColumns,
  createCustomTempProfileRow,
  ensureTrailingInputRow,
  extractCustomTempProfileRows,
  getCustomTempProfileKeyMds,
  populateCustomTempProfileFormArray
} from './custom-temp-profile.helper';

interface CustomTempProfileForm {
  type: TemperatureProfileType.CUSTOMTEMP;
  temperaturePoints: TemperaturePoint[];
}

@Component({
  selector: 'app-custom-temp-profile',
  template: `
    <form [formGroup]="form.formGroup">
      @if (form.formGroup.controls.temperaturePoints.errors?.lastPointAboveShoe) {
        <p class="validation-error">
          Must be specified from hanger: {{ currentString?.hangerMd.toFixed(2) }} {{ userUnits.longLengths }} and to or
          below shoe: {{ currentString?.shoeMd.toFixed(2) }} {{ userUnits.longLengths }}, but above well TD: {{ wellTd.toFixed(2) }} {{ userUnits.longLengths }}
        </p>
      }

      <app-units-grid-input
        [tableTitle]="'Custom Temperatures'"
        [rows]="customTempProfileRows"
        [columns]="columns"
        [userUnits]="userUnits"
        [enableScroll]="true"
        [scrollHeight]="'356px'"
        [allowDeleteRows]="true"
        [minRows]="1"
        [createRow]="createRow"
        [commitMode]="'debounced'"
        [commitDebounceMs]="300"
        (cellEdited)="onCellEdited()"
        (rowDeleted)="onRowDeleted($event)"
        (rowsReset)="onRowsReset()"
        (commitRequested)="onGridCommit($event)"
      />
    </form>
  `,
  providers: subformComponentProviders(CustomTempProfileComponent),
  standalone: false
})
export class CustomTempProfileComponent {

  @Input() wellType: WellType;
  @Input() currentString: any;
  @Input() userUnits: UserUnitsModel;
  @Input() wellTd: number;

  public readonly columns = createCustomTempProfileColumns();
  public readonly createRow = createCustomTempProfileRow;
  public customTempProfileRows = new FormArray<FormGroup>([]);

  private udtData: any;

  public form = createForm<CustomTempProfile, CustomTempProfileForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(TemperatureProfileType.CUSTOMTEMP),
      temperaturePoints: new UntypedFormControl(null)
    },
    toFormGroup: (obj: CustomTempProfile | null): CustomTempProfileForm | null => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        temperaturePoints: obj.temperaturePoints
      }
    }
  });

  constructor(private _udtService: UdtService, private _trajectoryService: TrajectoryService) {
    // ngx-sub-form patches `writeValue` (the CVA hook) onto `this` when `form` is created above.
    // For CVA sub-forms, initialization MUST happen only via writeValue to avoid racing against it.
    // Defer to next microtask to let ngx-sub-form's async form updates complete before reading values.
    const cva = this as unknown as { writeValue(value: CustomTempProfile | null): void };
    const applyWrittenValue = cva.writeValue.bind(cva);
    cva.writeValue = (value: CustomTempProfile | null): void => {
      applyWrittenValue(value);
      Promise.resolve().then(() => void this.setPoints());
    };
  }

  private async setPoints(): Promise<void> {
    this.form.formGroup.controls.temperaturePoints.addValidators(validateCustomProfile(this.currentString, this.wellTd));
    const existingPoints = this.form.formGroup.controls.temperaturePoints?.value;
    if (existingPoints?.length > 0) {
      populateCustomTempProfileFormArray(this.customTempProfileRows, existingPoints);
      return;
    }

    populateCustomTempProfileFormArray(this.customTempProfileRows, []);

    if (!this.udtData) {
      this.udtData = await lastValueFrom(this._udtService.getUndisturbedTemperaturePoints());
    }

    const points = await this.CreateInitialCustomTemperaturePoints();

    if (this.form.formGroup.controls.temperaturePoints?.value?.length > 0) {
      populateCustomTempProfileFormArray(this.customTempProfileRows, this.form.formGroup.controls.temperaturePoints.value);
      return;
    }

    this.form.formGroup.controls.temperaturePoints.setValue(points);

    populateCustomTempProfileFormArray(this.customTempProfileRows, points);
  }

  // Runs synchronously on blur so a new trailing row exists before Tab navigation looks for the next cell.
  public onCellEdited(): void {
    ensureTrailingInputRow(this.customTempProfileRows);
  }

  public onRowDeleted(index: number): void {
    if (index >= 0 && index < this.customTempProfileRows.length) {
      this.customTempProfileRows.removeAt(index);
    }
    ensureTrailingInputRow(this.customTempProfileRows);
    this.syncTemperaturePoints();
  }

  public async onRowsReset(): Promise<void> {
    const points = await this.CreateInitialCustomTemperaturePoints();
    populateCustomTempProfileFormArray(this.customTempProfileRows, points);
    this.form.formGroup.controls.temperaturePoints.setValue(points);
  }

  public onGridCommit(_event: UnitsGridCommitEvent): void {
    ensureTrailingInputRow(this.customTempProfileRows);
    this.syncTemperaturePoints();
  }

  private syncTemperaturePoints(): void {
    this.form.formGroup.controls.temperaturePoints.setValue(extractCustomTempProfileRows(this.customTempProfileRows));
  }

  private async CreateInitialCustomTemperaturePoints(): Promise<TemperaturePoint[]> {
    const keyMds = getCustomTempProfileKeyMds(this.wellType, this.currentString);
    const keyTvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds(keyMds));
    return createInitialCustomTemperaturePoints(keyMds, keyTvds, this.udtData);
  }
}
